import axios from 'axios';

/**
 * Stockfish API Configuration
 * 
 * The Stockfish engine is deployed at: https://stockfish-engine-3br2yqyefa-el.a.run.app
 * 
 * Configuration priority:
 * 1. Environment variable VITE_STOCKFISH_API_URL (if set)
 * 2. Default to deployed production URL
 * 
 * To use local Stockfish server, set in .env file:
 *   VITE_STOCKFISH_API_URL=http://localhost:8080
 */
const getStockfishApiUrl = () => {
    // Priority 1: Environment variable (explicit override)
    // This allows users to override for local development or different environments
    if (import.meta.env.VITE_STOCKFISH_API_URL) {
        const envUrl = import.meta.env.VITE_STOCKFISH_API_URL;
        console.log('[Stockfish API] Using URL from environment variable:', envUrl);
        return envUrl;
    }
    
    // Priority 2: Default to deployed production URL
    // Always use the deployed API unless explicitly overridden
    const deployedUrl = 'https://stockfish-engine-3br2yqyefa-el.a.run.app';
    console.log('[Stockfish API] Using deployed production URL:', deployedUrl);
    console.log('[Stockfish API] To use local server, set VITE_STOCKFISH_API_URL=http://localhost:8080 in .env file');
    return deployedUrl;
};

const STOCKFISH_API_URL = getStockfishApiUrl();

export interface StockfishAnalysis {
    bestMove?: string;
    evaluation?: number; // centipawns (positive = white advantage, negative = black advantage)
    depth?: number;
    pv?: string[]; // principal variation (best line)
    error?: string;
}

export interface MoveAnalysis {
    move: string;
    moveIndex?: number;
    moveUCI?: string;
    evaluation: number; // evaluation after this move (in pawns, not centipawns)
    bestMove?: string; // what Stockfish would play
    bestEvaluation?: number; // evaluation of best move
    isBestMove: boolean;
    isBlunder?: boolean;
    isMistake?: boolean;
    isInaccuracy?: boolean;
    evalLoss?: number; // evaluation loss in pawns
    comment?: string;
    explanation?: string;
    analysis?: string; // Rich analysis text from API
    evaluationDescription?: string; // Position evaluation description
    error?: string; // Error message if move is invalid
}

export interface GameAnalysisSummary {
    movesAnalyzed: number;
    bestMoves: number;
    inaccuracies: number;
    mistakes: number;
    blunders: number;
    accuracy: string; // Percentage as string
    summaryText: string;
}

export interface GameAnalysisResult {
    analyses: MoveAnalysis[];
    summary?: GameAnalysisSummary;
}

/**
 * Analyze a position to find the best move and evaluation
 * Uses the game/move endpoint with candidate moves to find the best one
 */
export async function analyzePosition(
    fen: string,
    depth: number = 15
): Promise<StockfishAnalysis> {
    try {
        // Try to use an /analyze endpoint if it exists
        try {
            const response = await axios.post(`${STOCKFISH_API_URL}/analyze`, {
                fen,
                depth,
            });
            
            if (response.data && !response.data.error) {
                return {
                    bestMove: response.data.bestMove,
                    evaluation: response.data.evaluation,
                    depth: response.data.depth,
                    pv: response.data.pv,
                };
            }
        } catch (analyzeError) {
            // If /analyze doesn't exist, fall through to alternative method
            console.log('Analyze endpoint not available, using alternative method');
        }

        // Alternative: Generate candidate moves and find the best one
        // This is less efficient but works with the game/move endpoint
        const { Chess } = await import('chess.js');
        const game = new Chess(fen);
        const moves = game.moves({ verbose: true });
        
        if (moves.length === 0) {
            return {
                error: 'No legal moves available',
            };
        }

        // Test all candidate moves to find the best one
        // For performance, we could limit this, but let's test all moves for accuracy
        const candidateMoves = moves.slice(0, Math.min(20, moves.length)); // Test up to 20 moves
        const moveEvaluations: Array<{ move: string; moveUCI: string; evaluation: number }> = [];

        for (const moveObj of candidateMoves) {
            try {
                const moveUCI = `${moveObj.from}${moveObj.to}${moveObj.promotion || ''}`;
                const response = await axios.post(`${STOCKFISH_API_URL}/game/move`, {
                    fen,
                    move: moveUCI,
                    skillLevel: 20, // Use max skill for analysis
                    depth,
                });

                if (response.data && response.data.evaluation !== null && response.data.evaluation !== undefined) {
                    moveEvaluations.push({
                        move: moveObj.san,
                        moveUCI: moveUCI,
                        evaluation: response.data.evaluation,
                    });
                }
            } catch (error) {
                console.error(`Error testing move ${moveObj.san}:`, error);
            }
        }

        if (moveEvaluations.length === 0) {
            return {
                error: 'Could not evaluate moves',
            };
        }

        // Find the best move
        // Evaluation is from the perspective of the player to move
        // Positive = good for white, negative = good for black
        const isWhite = game.turn() === 'w';
        let bestMoveData;
        
        if (isWhite) {
            // White wants highest evaluation
            const bestEval = Math.max(...moveEvaluations.map(m => m.evaluation));
            bestMoveData = moveEvaluations.find(m => m.evaluation === bestEval);
        } else {
            // Black wants lowest (most negative) evaluation
            const bestEval = Math.min(...moveEvaluations.map(m => m.evaluation));
            bestMoveData = moveEvaluations.find(m => m.evaluation === bestEval);
        }

        return {
            bestMove: bestMoveData?.move,
            evaluation: bestMoveData?.evaluation ?? 0,
            depth,
        };
    } catch (error) {
        console.error('Stockfish API error:', error);
        return {
            error: error instanceof Error ? error.message : 'Failed to analyze position',
        };
    }
}

/**
 * Analyze a complete game using batch endpoint (much faster)
 * Returns analysis for each move with summary
 */
export async function analyzeGameBatch(
    moves: string[],
    initialFen: string = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    depth: number = 15,
    skillLevel: number = 20
): Promise<GameAnalysisResult> {
    try {
        console.log(`[Batch Analysis] Starting batch analysis for ${moves.length} moves`);
        console.log(`[Batch Analysis] API URL: ${STOCKFISH_API_URL}/game/analyze`);
        console.log(`[Batch Analysis] Request payload:`, { 
            movesCount: moves.length, 
            initialFen, 
            depth, 
            skillLevel 
        });
        
        const response = await axios.post(`${STOCKFISH_API_URL}/game/analyze`, {
            moves,
            initialFen,
            depth,
            skillLevel,
        }, {
            timeout: 300000, // 5 minute timeout for batch analysis (matches server timeout)
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.data || !response.data.analyses) {
            throw new Error('Invalid response from batch analysis endpoint: missing analyses');
        }

        console.log(`[Batch Analysis] Received ${response.data.analyses.length} analyses`);

        // Map the API response to our MoveAnalysis format
        const analyses: MoveAnalysis[] = response.data.analyses.map((analysis: any) => {
            return {
                move: analysis.move,
                moveIndex: analysis.moveIndex,
                moveUCI: analysis.moveUCI,
                evaluation: analysis.evaluation || 0,
                bestMove: analysis.bestMove,
                bestEvaluation: analysis.bestEvaluation,
                isBestMove: analysis.isBestMove || false,
                isBlunder: analysis.isBlunder || false,
                isMistake: analysis.isMistake || false,
                isInaccuracy: analysis.isInaccuracy || false,
                evalLoss: analysis.evalLoss,
                comment: analysis.isBestMove ? 'Best move' :
                         analysis.isBlunder ? 'Blunder' :
                         analysis.isMistake ? 'Mistake' :
                         analysis.isInaccuracy ? 'Inaccuracy' :
                         'Reasonable move',
                explanation: analysis.analysis || analysis.explanation, // Use API's analysis text
                analysis: analysis.analysis, // Rich analysis from API
                evaluationDescription: analysis.evaluationDescription,
                error: analysis.error,
            };
        });

        // Extract summary if available
        const summary: GameAnalysisSummary | undefined = response.data.summary ? {
            movesAnalyzed: response.data.summary.movesAnalyzed,
            bestMoves: response.data.summary.bestMoves,
            inaccuracies: response.data.summary.inaccuracies,
            mistakes: response.data.summary.mistakes,
            blunders: response.data.summary.blunders,
            accuracy: response.data.summary.accuracy,
            summaryText: response.data.summary.summaryText,
        } : undefined;

        console.log(`[Batch Analysis] Completed: ${analyses.length} moves analyzed`);
        if (summary) {
            console.log(`[Batch Analysis] Summary: ${summary.summaryText}`);
        }

        return {
            analyses,
            summary,
        };
    } catch (error: any) {
        console.error('[Batch Analysis] Error:', error);
        console.error('[Batch Analysis] Error details:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
            url: `${STOCKFISH_API_URL}/game/analyze`
        });
        
        // DO NOT fall back to sequential analysis - it causes CORS errors and too many API calls
        // Instead, throw a clear error
        const errorMessage = error?.response?.data?.error || 
                           error?.response?.data?.details ||
                           error?.message || 
                           'Failed to analyze game using batch endpoint';
        
        throw new Error(`Batch analysis failed: ${errorMessage}. Please check the API endpoint and try again.`);
    }
}

/**
 * Analyze a complete game (sequence of moves) - Sequential method (fallback)
 * Returns analysis for each move
 */
export async function analyzeGameSequential(
    moves: string[],
    initialFen: string = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKQ - 0 1',
    depth: number = 15
): Promise<MoveAnalysis[]> {
    const { Chess } = await import('chess.js');
    const game = new Chess(initialFen);
    const analyses: MoveAnalysis[] = [];

    for (let i = 0; i < moves.length; i++) {
        const move = moves[i];
        const positionBeforeMove = game.fen();
        
        try {
            // Make the move locally to get the position after
            const moveResult1 = game.move(move);
            if (!moveResult1) {
                analyses.push({
                    move,
                    evaluation: 0,
                    isBestMove: false,
                    comment: 'Invalid move',
                });
                continue;
            }

            // Create a temporary game to convert move to UCI before making the move
            const tempGameBeforeMove = new Chess(positionBeforeMove);
            const moveResult = tempGameBeforeMove.move(move);
            if (!moveResult) {
                analyses.push({
                    move,
                    evaluation: 0,
                    isBestMove: false,
                    comment: 'Invalid move',
                });
                continue;
            }
            
            const moveUCI = `${moveResult.from}${moveResult.to}${moveResult.promotion || ''}`;
            const positionAfterMove = game.fen();
            const isWhite = tempGameBeforeMove.turn() === 'b'; // If black's turn now, white just moved

            // Analyze position before move to find best move and its evaluation
            console.log(`[Move ${i + 1}] Analyzing position before move: ${move}`);
            const bestMoveAnalysis = await analyzePosition(positionBeforeMove, depth);

            if (bestMoveAnalysis.error) {
                console.warn(`[Move ${i + 1}] Error analyzing position: ${bestMoveAnalysis.error}`);
            }

            // Get evaluation after the played move
            console.log(`[Move ${i + 1}] Getting evaluation for move: ${move} (UCI: ${moveUCI})`);
            const afterMoveResponse = await axios.post(`${STOCKFISH_API_URL}/game/move`, {
                fen: positionBeforeMove,
                move: moveUCI,
                skillLevel: 20,
                depth,
            });

            const actualEval = afterMoveResponse.data?.evaluation ?? 0;
            const bestEval = bestMoveAnalysis.evaluation ?? 0;

            console.log(`[Move ${i + 1}] Move: ${move}, Best: ${bestMoveAnalysis.bestMove}, BestEval: ${bestEval}, ActualEval: ${actualEval}, isWhite: ${isWhite}`);

            // Calculate evaluation loss
            // Evaluation is from the perspective of the player who just moved
            // For white: positive eval is good, so loss = bestEval - actualEval
            // For black: negative eval is good, so loss = actualEval - bestEval
            const evalLoss = Math.abs(bestEval - actualEval);

            console.log(`[Move ${i + 1}] evalLoss: ${evalLoss}`);

            // Compare moves - convert best move to UCI for comparison
            let isBestMove = false;
            if (bestMoveAnalysis.bestMove) {
                // Try to convert best move to UCI for comparison
                const tempGameForBest = new Chess(positionBeforeMove);
                try {
                    const bestMoveResult = tempGameForBest.move(bestMoveAnalysis.bestMove);
                    if (bestMoveResult) {
                        const bestMoveUCI = `${bestMoveResult.from}${bestMoveResult.to}${bestMoveResult.promotion || ''}`;
                        isBestMove = bestMoveUCI === moveUCI || bestMoveAnalysis.bestMove === move;
                    }
                } catch {
                    // If conversion fails, just compare SAN
                    isBestMove = bestMoveAnalysis.bestMove === move;
                }
            }
            let isBlunder = false;
            let isMistake = false;
            let isInaccuracy = false;
            let comment = '';
            let explanation = '';

            if (!isBestMove && bestMoveAnalysis.bestMove && !bestMoveAnalysis.error) {
                // Only classify as mistake/blunder if we have a valid best move
                if (evalLoss > 300) {
                    isBlunder = true;
                    comment = 'Blunder';
                    explanation = generateBlunderExplanation(move, bestMoveAnalysis.bestMove, evalLoss);
                    console.log(`[Move ${i + 1}] BLUNDER detected: evalLoss=${evalLoss}`);
                } else if (evalLoss > 100) {
                    isMistake = true;
                    comment = 'Mistake';
                    explanation = generateMistakeExplanation(move, bestMoveAnalysis.bestMove, evalLoss);
                    console.log(`[Move ${i + 1}] MISTAKE detected: evalLoss=${evalLoss}`);
                } else if (evalLoss > 50) {
                    isInaccuracy = true;
                    comment = 'Inaccuracy';
                    explanation = `A better move was ${bestMoveAnalysis.bestMove}. This move slightly weakens your position by approximately ${(evalLoss / 100).toFixed(1)} pawns.`;
                    console.log(`[Move ${i + 1}] INACCURACY detected: evalLoss=${evalLoss}`);
                } else {
                    comment = 'Reasonable move';
                }
            } else if (isBestMove) {
                comment = 'Best move';
                console.log(`[Move ${i + 1}] BEST MOVE`);
            } else {
                comment = 'Reasonable move';
            }

            analyses.push({
                move,
                evaluation: actualEval,
                bestMove: bestMoveAnalysis.bestMove,
                isBestMove,
                isBlunder,
                isMistake,
                isInaccuracy,
                comment,
                explanation,
            });

            // Small delay to avoid rate limiting
            if (i < moves.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        } catch (error) {
            console.error(`Error analyzing move ${i + 1}:`, error);
            analyses.push({
                move,
                evaluation: 0,
                isBestMove: false,
                comment: 'Analysis error',
            });
        }
    }

    return analyses;
}


/**
 * Main game analysis function - uses batch endpoint by default
 * Returns both analyses and summary
 */
export async function analyzeGame(
    moves: string[],
    initialFen: string = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    depth: number = 15,
    skillLevel: number = 20
): Promise<GameAnalysisResult> {
    return analyzeGameBatch(moves, initialFen, depth, skillLevel);
}

/**
 * Legacy function for backward compatibility - returns just analyses array
 */
export async function analyzeGameLegacy(
    moves: string[],
    initialFen: string = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    depth: number = 15
): Promise<MoveAnalysis[]> {
    const result = await analyzeGameBatch(moves, initialFen, depth);
    return result.analyses;
}

/**
 * Generate explanation for why a move is a blunder
 */
function generateBlunderExplanation(
    playedMove: string,
    bestMove: string,
    evalLoss: number
): string {
    const pawnsLost = (evalLoss / 100).toFixed(1);
    
    if (evalLoss > 500) {
        return `This is a severe blunder! The best move was ${bestMove}. You lost approximately ${pawnsLost} pawns of advantage. This move significantly weakens your position and may lead to material loss or checkmate threats.`;
    } else {
        return `This is a blunder. The best move was ${bestMove}. You lost approximately ${pawnsLost} pawns of advantage. This move weakens your position considerably.`;
    }
}

/**
 * Generate explanation for why a move is a mistake
 */
function generateMistakeExplanation(
    playedMove: string,
    bestMove: string,
    evalLoss: number
): string {
    const pawnsLost = (evalLoss / 100).toFixed(1);
    return `This is a mistake. The best move was ${bestMove}. You lost approximately ${pawnsLost} pawns of advantage. While not catastrophic, this move gives your opponent a better position.`;
}

